import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, InfoWindowF, MarkerF, PolylineF, useLoadScript } from '@react-google-maps/api';
import axios from 'axios';
import { ChevronDown, Maximize, MapPin, Navigation, Phone, RefreshCw, Search } from 'react-feather';
import moment from 'moment';
import styles from './liveDrivers.module.css';
import { authData, withAdminAuth } from '../../components/GetAuth';
import PageHeader from '../../components/PageHeader';
import { useWebsite } from '../../../context/WebsiteContext';
import { useSiteBranding } from '../../../hooks/useSiteBranding';

const REFRESH_MS = 10000;
const STALE_MINUTES = 15;
const ANIMATE_MS = 1500;
const SNAP_DISTANCE_M = 2000;
const TRAIL_LENGTH = 20;
const LABEL_MIN_ZOOM = 9;
const UK_CENTER = { lat: 52.3555, lng: -1.1743 };

// Feather-style 24x24 stroke glyphs drawn inside the marker head.
const GLYPHS = {
  truck:
    '<rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>',
  package:
    '<line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>',
};

const TYPE_META = {
  3: { key: 'pickup', label: 'Pickup', color: '#e64f22', glyph: 'truck' },
  4: { key: 'delivery', label: 'Delivery', color: '#1d9e75', glyph: 'package' },
};
const STALE_COLOR = '#9a9aa5';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pickup', label: 'Pickup' },
  { key: 'delivery', label: 'Delivery' },
];

const MAP_OPTIONS = {
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  clickableIcons: false,
};

// Marker canvas: 64x72, pin head centred at (32,30), tip at (32,60).
const PIN_W = 64;
const PIN_H = 72;
const PIN_TIP_Y = 60;
const iconCache = new Map();

function svgUrl(svg) {
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
}

function driverIconUrl(color, glyph, bearing) {
  const key = `${color}|${glyph}|${bearing}`;
  if (iconCache.has(key)) return iconCache.get(key);
  const arrow =
    bearing === null
      ? ''
      : `<g transform="rotate(${bearing} 32 30)"><path d="M32 1 L38.5 9 L32 7 L25.5 9 Z" fill="${color}" stroke="#fff" stroke-width="1.5" stroke-linejoin="round"/></g>`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${PIN_W}" height="${PIN_H}" viewBox="0 0 ${PIN_W} ${PIN_H}">
<defs><filter id="s" x="-30%" y="-30%" width="160%" height="160%"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000" flood-opacity="0.3"/></filter></defs>
<g filter="url(#s)"><path transform="translate(10 8)" d="M22 2C11 2 2 10.8 2 21.6c0 14.2 17.4 29 18.9 30.3a1.7 1.7 0 0 0 2.2 0C24.6 50.6 42 35.8 42 21.6 42 10.8 33 2 22 2z" fill="${color}" stroke="#fff" stroke-width="2.5"/></g>
<g transform="translate(23 21) scale(0.75)" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${GLYPHS[glyph]}</g>
${arrow}
</svg>`;
  const url = svgUrl(svg);
  iconCache.set(key, url);
  return url;
}

function stopIconUrl(color) {
  const key = `stop|${color}`;
  if (iconCache.has(key)) return iconCache.get(key);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
<defs><filter id="s" x="-30%" y="-30%" width="160%" height="160%"><feDropShadow dx="0" dy="1" stdDeviation="1.2" flood-color="#000" flood-opacity="0.25"/></filter></defs>
<rect x="3" y="3" width="22" height="22" rx="6" fill="#fff" stroke="${color}" stroke-width="2.5" filter="url(#s)"/></svg>`;
  const url = svgUrl(svg);
  iconCache.set(key, url);
  return url;
}

function toLatLng(lat, lng) {
  const la = parseFloat(lat);
  const ln = parseFloat(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln) || (la === 0 && ln === 0)) return null;
  return { lat: la, lng: ln };
}

const toRad = (deg) => (deg * Math.PI) / 180;

function distanceM(a, b) {
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function bearingDeg(a, b) {
  const y = Math.sin(toRad(b.lng - a.lng)) * Math.cos(toRad(b.lat));
  const x =
    Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) -
    Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(toRad(b.lng - a.lng));
  return (Math.atan2(y, x) * 180) / Math.PI + 360;
}

function samePos(a, b) {
  return a && b && Math.abs(a.lat - b.lat) < 1e-7 && Math.abs(a.lng - b.lng) < 1e-7;
}

function minutesBetween(from, to) {
  if (!from || !to) return null;
  return moment(to).diff(moment(from), 'minutes');
}

function formatAgo(minutes) {
  if (minutes === null) return 'Unknown';
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)} h ago`;
  return `${Math.floor(minutes / 1440)} d ago`;
}

function initial(name) {
  return (name || '?').trim().charAt(0).toUpperCase();
}

function firstName(name) {
  return (name || '').trim().split(/\s+/)[0] || '';
}

function LiveDrivers() {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const { websiteData } = useWebsite();
  useSiteBranding('Live Drivers', websiteData);

  // Same loader id/options as the other admin maps, so the script is shared.
  const { isLoaded, loadError } = useLoadScript({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY,
    libraries: ['places'],
  });

  const [drivers, setDrivers] = useState([]);
  const [serverTime, setServerTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [showOffline, setShowOffline] = useState(false);
  const [follow, setFollow] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [zoom, setZoom] = useState(6);
  // Animated marker positions, keyed by driver id.
  const [animPos, setAnimPos] = useState({});

  const mapRef = useRef(null);
  const fittedRef = useRef(false);
  const inFlightRef = useRef(false);
  const displayRef = useRef({});
  const tweensRef = useRef({});
  const rafRef = useRef(null);
  const trailsRef = useRef({});
  const bearingsRef = useRef({});

  const fetchDrivers = useCallback(() => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setRefreshing(true);
    axios
      .get(process.env.REACT_APP_API_URL + 'v1/dashboard/live-drivers', {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        },
      })
      .then((response) => {
        if (response.data.status === 'SUCCESS') {
          setDrivers(response.data.data?.drivers || []);
          setServerTime(response.data.data?.server_time || null);
          setFetchFailed(false);
        } else {
          setFetchFailed(true);
        }
      })
      .catch(() => setFetchFailed(true))
      .finally(() => {
        inFlightRef.current = false;
        setLoading(false);
        setRefreshing(false);
      });
  }, [API_TOKEN]);

  useEffect(() => {
    fetchDrivers();
    const poll = setInterval(() => {
      if (!document.hidden) fetchDrivers();
    }, REFRESH_MS);
    const onVisible = () => {
      if (!document.hidden) fetchDrivers();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchDrivers]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  // Normalise API rows once per fetch.
  const allDrivers = useMemo(
    () =>
      drivers.map((d) => {
        const meta = TYPE_META[parseInt(d.type)] || TYPE_META[3];
        const position = toLatLng(d.current_lat, d.current_lng);
        const lastSeenMin = minutesBetween(d.updated_at, serverTime);
        const online = String(d.is_online) === '1';
        const jobs = (d.jobs || []).map((job) => ({ ...job, position: toLatLng(job.lat, job.lng) }));
        return {
          ...d,
          meta,
          position,
          online,
          jobs,
          lastSeenMin,
          stale: online && lastSeenMin !== null && lastSeenMin > STALE_MINUTES,
        };
      }),
    [drivers, serverTime]
  );

  // One rAF loop glides every moving marker from its shown position to its new one.
  const runTweens = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    const step = (now) => {
      let moving = false;
      Object.entries(tweensRef.current).forEach(([id, t]) => {
        const p = Math.min((now - t.start) / ANIMATE_MS, 1);
        displayRef.current[id] = {
          lat: t.from.lat + (t.to.lat - t.from.lat) * p,
          lng: t.from.lng + (t.to.lng - t.from.lng) * p,
        };
        if (p < 1) moving = true;
        else delete tweensRef.current[id];
      });
      setAnimPos({ ...displayRef.current });
      if (moving) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }, []);

  useEffect(() => {
    let changed = false;
    const now = performance.now();
    const seen = new Set();

    allDrivers.forEach((d) => {
      if (!d.online || !d.position) return;
      seen.add(String(d.id));
      const shown = displayRef.current[d.id];
      const trail = trailsRef.current[d.id] || (trailsRef.current[d.id] = []);
      const last = trail[trail.length - 1];

      if (!last || !samePos(last, d.position)) {
        if (last && distanceM(last, d.position) > 5) bearingsRef.current[d.id] = bearingDeg(last, d.position) % 360;
        trail.push(d.position);
        if (trail.length > TRAIL_LENGTH) trail.shift();
      }

      if (!shown || distanceM(shown, d.position) > SNAP_DISTANCE_M) {
        delete tweensRef.current[d.id];
        displayRef.current[d.id] = d.position;
        changed = true;
      } else if (!samePos(shown, d.position)) {
        tweensRef.current[d.id] = { from: shown, to: d.position, start: now };
        changed = true;
      }
    });

    // Drop drivers that went offline or lost their location.
    Object.keys(displayRef.current).forEach((id) => {
      if (!seen.has(id)) {
        delete displayRef.current[id];
        delete tweensRef.current[id];
        changed = true;
      }
    });

    if (!changed) return;
    if (Object.keys(tweensRef.current).length > 0) runTweens();
    else setAnimPos({ ...displayRef.current });
  }, [allDrivers, runTweens]);

  const query = search.trim().toLowerCase();
  const matches = (d) =>
    (filter === 'all' || d.meta.key === filter) &&
    (!query || `${d.name} ${d.driver_id || ''}`.toLowerCase().includes(query));

  const onlineDrivers = allDrivers.filter((d) => d.online);
  const offlineDrivers = allDrivers.filter((d) => !d.online);
  const visibleOnline = onlineDrivers.filter(matches);
  const visibleOffline = offlineDrivers.filter(matches);
  const mapDrivers = visibleOnline.filter((d) => animPos[d.id]);
  const selected = allDrivers.find((d) => d.id === selectedId) || null;
  const selectedPos = selected ? animPos[selected.id] || null : null;

  const counts = {
    all: onlineDrivers.length,
    pickup: onlineDrivers.filter((d) => d.meta.key === 'pickup').length,
    delivery: onlineDrivers.filter((d) => d.meta.key === 'delivery').length,
  };
  const onJobCount = onlineDrivers.filter((d) => d.jobs.length > 0).length;

  const fitToDrivers = useCallback((list) => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const points = list.filter((d) => d.online && d.position).map((d) => d.position);
    if (points.length === 0) {
      map.setCenter(UK_CENTER);
      map.setZoom(6);
      return;
    }
    if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(13);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 60);
  }, []);

  // Fit once, when both the map and first data are ready. Refreshes never re-fit.
  useEffect(() => {
    if (!fittedRef.current && mapReady && !loading) {
      fittedRef.current = true;
      fitToDrivers(allDrivers);
    }
  }, [mapReady, loading, allDrivers, fitToDrivers]);

  // Follow mode keeps the selected driver centred while they move.
  useEffect(() => {
    if (follow && selectedPos && mapRef.current) mapRef.current.setCenter(selectedPos);
  }, [follow, selectedPos]);

  const selectDriver = (driver) => {
    setSelectedId(driver.id);
    setFollow(false);
    const pos = animPos[driver.id];
    if (pos && mapRef.current) {
      mapRef.current.panTo(pos);
      if (mapRef.current.getZoom() < 12) mapRef.current.setZoom(13);
    }
  };

  const driverIcon = (d) => {
    const scale = d.id === selectedId ? 1.2 : 1;
    const bearing = bearingsRef.current[d.id];
    const url = driverIconUrl(
      d.stale ? STALE_COLOR : d.meta.color,
      d.meta.glyph,
      bearing === undefined ? null : Math.round(bearing / 15) * 15
    );
    return {
      url,
      scaledSize: new window.google.maps.Size(PIN_W * scale, PIN_H * scale),
      anchor: new window.google.maps.Point((PIN_W / 2) * scale, PIN_TIP_Y * scale),
      labelOrigin: new window.google.maps.Point((PIN_W / 2) * scale, PIN_H * scale + 4),
    };
  };

  const stopIcon = (color) => ({
    url: stopIconUrl(color),
    scaledSize: new window.google.maps.Size(28, 28),
    anchor: new window.google.maps.Point(14, 14),
    labelOrigin: new window.google.maps.Point(14, 14),
  });

  const routePath = selectedPos
    ? [selectedPos, ...selected.jobs.filter((j) => j.position).map((j) => j.position)]
    : [];
  const trailPath =
    selectedPos && trailsRef.current[selected.id]
      ? [...trailsRef.current[selected.id].slice(0, -1), selectedPos]
      : [];

  const statusText = (d) => {
    if (!d.online) return `Last seen ${formatAgo(d.lastSeenMin).toLowerCase()}`;
    if (!d.position) return 'Location unavailable';
    if (d.jobs.length > 0) return `On job · ${d.jobs.length} stop${d.jobs.length > 1 ? 's' : ''}`;
    return 'Idle';
  };

  const renderRow = (d) => (
    <button
      type="button"
      key={d.id}
      className={`${styles.DriverRow} ${d.id === selectedId ? styles.DriverRowActive : ''} ${!d.online ? styles.DriverRowOffline : ''}`}
      onClick={() => selectDriver(d)}
    >
      <span
        className={styles.Avatar}
        style={{ '--avatar': d.online ? (d.stale ? STALE_COLOR : d.meta.color) : STALE_COLOR }}
      >
        {initial(d.name)}
      </span>
      <span className={styles.DriverInfo}>
        <span className={styles.DriverName}>{d.name}</span>
        <span className={styles.DriverStatus}>
          {d.online && d.stale && <span className={styles.StaleDot} title="Location may be out of date" />}
          {statusText(d)}
        </span>
      </span>
      <span className={`${styles.TypeBadge} ${styles[`Type_${d.meta.key}`]}`}>{d.meta.label}</span>
    </button>
  );

  return (
    <div className={styles.MainDiv}>
      <PageHeader title="Live Drivers" trail={[{ label: 'Dashboard', to: '/dashboard' }]} />

      <div className={styles.MainCard}>
        {/* Toolbar */}
        <div className={styles.Toolbar}>
          <div className={styles.Chips}>
            {FILTERS.map((f) => (
              <button
                type="button"
                key={f.key}
                className={`${styles.Chip} ${filter === f.key ? styles.ChipActive : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.key !== 'all' && (
                  <span className={styles.ChipDot} style={{ '--dot': f.key === 'pickup' ? TYPE_META[3].color : TYPE_META[4].color }} />
                )}
                {f.label}
                <span className={styles.ChipCount}>{counts[f.key]}</span>
              </button>
            ))}
          </div>

          <div className={styles.Summary}>
            <span className={`${styles.SummaryPill} ${styles.PillOnline}`}>{`${onlineDrivers.length} online`}</span>
            <span className={styles.SummaryPill}>{`${onJobCount} on a job`}</span>
            <span className={styles.SummaryPill}>{`${offlineDrivers.length} offline`}</span>
          </div>

          <div className={styles.RefreshWrap}>
            <span
              className={`${styles.Live} ${fetchFailed ? styles.LiveError : ''}`}
              title={fetchFailed ? 'Last update failed, retrying' : `Updating every ${REFRESH_MS / 1000}s`}
            >
              <span className={styles.LiveDot} />
              {fetchFailed ? 'Reconnecting' : 'Live'}
            </span>
            <button
              type="button"
              className={styles.RefreshBtn}
              onClick={fetchDrivers}
              disabled={refreshing}
              aria-label="Refresh driver locations"
            >
              <RefreshCw className={refreshing ? styles.Spin : undefined} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className={styles.Layout}>
          {/* Map */}
          <div className={`${styles.Card} ${styles.MapCard}`}>
            {loadError ? (
              <p className={styles.MapMessage}>The map couldn't load. Check the Google Maps API key.</p>
            ) : !isLoaded || loading ? (
              <div className={styles.SkeletonMap} />
            ) : (
              <GoogleMap
                mapContainerClassName={styles.Map}
                center={UK_CENTER}
                zoom={6}
                options={MAP_OPTIONS}
                onLoad={(map) => {
                  mapRef.current = map;
                  setMapReady(true);
                }}
                onUnmount={() => {
                  mapRef.current = null;
                  fittedRef.current = false;
                  setMapReady(false);
                }}
                onZoomChanged={() => {
                  if (mapRef.current) setZoom(mapRef.current.getZoom());
                }}
                onDragStart={() => setFollow(false)}
                onClick={() => setSelectedId(null)}
              >
                {trailPath.length > 1 && (
                  <PolylineF
                    path={trailPath}
                    options={{ strokeColor: selected.meta.color, strokeOpacity: 0.5, strokeWeight: 4, zIndex: 1 }}
                  />
                )}

                {routePath.length > 1 && (
                  <PolylineF
                    path={routePath}
                    options={{
                      strokeOpacity: 0,
                      zIndex: 2,
                      icons: [
                        {
                          icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.9, strokeColor: selected.meta.color, scale: 3 },
                          offset: '0',
                          repeat: '12px',
                        },
                      ],
                    }}
                  />
                )}

                {selectedPos &&
                  selected.jobs
                    .filter((j) => j.position)
                    .map((job, idx) => (
                      <MarkerF
                        key={`stop-${job.id}`}
                        position={job.position}
                        icon={stopIcon(selected.meta.color)}
                        label={{ text: String(idx + 1), color: selected.meta.color, fontSize: '12px', fontWeight: '700' }}
                        title={`${job.booking_id} · ${job.name || ''}\n${job.address || ''}`}
                        zIndex={500}
                      />
                    ))}

                {mapDrivers.map((d) => (
                  <MarkerF
                    key={d.id}
                    position={animPos[d.id]}
                    icon={driverIcon(d)}
                    label={
                      zoom >= LABEL_MIN_ZOOM || d.id === selectedId
                        ? { text: firstName(d.name), className: styles.MarkerLabel, color: '#2f2f2f', fontSize: '12px', fontWeight: '600' }
                        : undefined
                    }
                    title={`${d.name} (${d.meta.label})`}
                    zIndex={d.id === selectedId ? 1000 : 600}
                    onClick={() => selectDriver(d)}
                  />
                ))}

                {selectedPos && selected.online && (
                  <InfoWindowF
                    position={selectedPos}
                    options={{ pixelOffset: new window.google.maps.Size(0, -PIN_TIP_Y * 1.2 + 4), disableAutoPan: follow }}
                    onCloseClick={() => {
                      setSelectedId(null);
                      setFollow(false);
                    }}
                  >
                    <div className={styles.Info}>
                      <p className={styles.InfoName}>{selected.name}</p>
                      <p className={styles.InfoMeta}>
                        <span className={`${styles.TypeBadge} ${styles[`Type_${selected.meta.key}`]}`}>
                          {selected.meta.label}
                        </span>
                        {selected.driver_id && <span>{selected.driver_id}</span>}
                      </p>
                      {selected.phone && (
                        <a className={styles.InfoLine} href={`tel:${selected.phone}`}>
                          <Phone /> {selected.phone}
                        </a>
                      )}
                      <p className={styles.InfoLine}>
                        <MapPin /> {`Location updated ${formatAgo(selected.lastSeenMin).toLowerCase()}`}
                      </p>
                      <button
                        type="button"
                        className={`${styles.FollowBtn} ${follow ? styles.FollowBtnOn : ''}`}
                        onClick={() => setFollow((v) => !v)}
                        aria-pressed={follow}
                      >
                        <Navigation />
                        {follow ? 'Following' : 'Follow driver'}
                      </button>
                      {selected.jobs.length > 0 ? (
                        <ol className={styles.InfoJobs}>
                          {selected.jobs.slice(0, 4).map((job) => (
                            <li key={job.id}>
                              <strong>{job.booking_id}</strong> {job.address}
                            </li>
                          ))}
                          {selected.jobs.length > 4 && <li className={styles.InfoMore}>{`+${selected.jobs.length - 4} more`}</li>}
                        </ol>
                      ) : (
                        <p className={styles.InfoIdle}>No active jobs right now.</p>
                      )}
                    </div>
                  </InfoWindowF>
                )}
              </GoogleMap>
            )}

            {isLoaded && !loading && !loadError && (
              <div className={styles.MapControls}>
                <button
                  type="button"
                  className={styles.FitBtn}
                  onClick={() => {
                    setFollow(false);
                    fitToDrivers(visibleOnline);
                  }}
                >
                  <Maximize />
                  <span>Fit all</span>
                </button>
              </div>
            )}

            {isLoaded && !loading && onlineDrivers.length === 0 && (
              <div className={styles.MapEmpty}>No drivers are online right now.</div>
            )}
          </div>

          {/* Driver list */}
          <div className={`${styles.Card} ${styles.ListCard}`}>
            <div className={styles.SearchBox}>
              <Search />
              <input
                type="search"
                placeholder="Search name or driver ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className={styles.ListScroll}>
              {loading ? (
                <div className={styles.SkeletonList}>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div className={styles.SkeletonRow} key={i} />
                  ))}
                </div>
              ) : (
                <>
                  <p className={styles.ListHeading}>{`Online (${visibleOnline.length})`}</p>
                  {visibleOnline.length === 0 ? (
                    <p className={styles.EmptyState}>
                      {query || filter !== 'all' ? 'No online drivers match.' : 'No drivers are online right now.'}
                    </p>
                  ) : (
                    visibleOnline.map(renderRow)
                  )}

                  {visibleOffline.length > 0 && (
                    <>
                      <button
                        type="button"
                        className={`${styles.ListHeading} ${styles.OfflineToggle}`}
                        onClick={() => setShowOffline((v) => !v)}
                        aria-expanded={showOffline}
                      >
                        {`Offline (${visibleOffline.length})`}
                        <ChevronDown className={showOffline ? styles.ChevronOpen : undefined} />
                      </button>
                      {showOffline && visibleOffline.map(renderRow)}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withAdminAuth(LiveDrivers);
